import { useQuery } from '@tanstack/react-query';
import { formatNumber } from '@/utils/number';
import { PCMP_PERMISSION } from '../../api/pcmpAccess.api';
import { downloadPcmpMisQueueXlsx, pcmpMisCountsQueryOptions } from '../../api/pcmpMis.api';
import { PCMP_MIS_TAB_COUNT, PCMP_MIS_TAB_LABELS } from '../../constants/pcmpMis.constants';
import { usePcmpMisFilters } from '../../hooks/usePcmpMisFilters';
import { usePcmpPermissions } from '../../hooks/usePcmpPermissions';
import { PCMP_MIS_VIEWS } from '../../types/pcmpMis.types';
import { PcmpMisBatchesView } from '../PcmpMisBatches/PcmpMisBatchesView';
import { PcmpExportButton } from '../PcmpQueue/PcmpExportButton';
import { PcmpQueueFilterBar } from '../PcmpQueue/PcmpQueueFilterBar';
import { PcmpQueueTabs } from '../PcmpQueue/PcmpQueueTabs';
import { PcmpMisProposalsView } from './PcmpMisProposalsView';

export function PcmpMisQueueTable() {
  const controller = usePcmpMisFilters();
  const { tab, appliedFilters, queueParams } = controller;
  const { can } = usePcmpPermissions();
  // A route batch exports from its own page; the Batches tab has no proposal list.
  const canExport = can(PCMP_PERMISSION.VIEW_ALL, PCMP_PERMISSION.EXPORT) && queueParams !== null;

  const { data: counts } = useQuery(pcmpMisCountsQueryOptions(appliedFilters));

  return (
    <div className="flex flex-col gap-4 lg:h-[calc(100dvh-88px)]">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold">MIS Verification Queue</h1>
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {counts
              ? `${formatNumber(counts.pending)} awaiting verification · ${formatNumber(
                  counts.explained,
                )} explanations to review`
              : ' '}
          </p>
        </div>
        {canExport && queueParams ? (
          <PcmpExportButton
            title={`${PCMP_MIS_TAB_LABELS[queueParams.tab]}, under the filters below`}
            download={(date) => downloadPcmpMisQueueXlsx(queueParams.tab, appliedFilters, date)}
          />
        ) : null}
      </div>

      <PcmpQueueTabs
        tabs={PCMP_MIS_VIEWS}
        labels={PCMP_MIS_TAB_LABELS}
        value={tab}
        countOf={counts ? (value) => PCMP_MIS_TAB_COUNT[value](counts) : undefined}
        onChange={controller.setTab}
      />

      <PcmpQueueFilterBar controller={controller} idPrefix="pcmp-mis" />

      {queueParams ? (
        <PcmpMisProposalsView controller={controller} params={queueParams} counts={counts} />
      ) : (
        <PcmpMisBatchesView controller={controller} />
      )}
    </div>
  );
}
